import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import user, { RegisterUser, User, UserResponse } from '../../services/user';
import { userSchema } from '../../validation/schemas/userSchema';
import ModalComponent from '../../components/ModalComponent';
import Input from '../../components/Form/Input';
import submit from '../../utils/submit';
import Card from '../../components/Card';
import Paginator from '../../components/Paginator';
import Table from '../../components/Table/Table';
import ActionButton from '../../components/Table/ActionButton';
import Select from '../../components/Form/Select';
import { userRoleOptions } from '../../utils/user.utils';
import { userEditSchema } from '../../validation/schemas/userEditSchema';
import SwitchButton from '../../components/Buttons/SwitchButton';
import MultiInput from '../../components/Form/MultiInput';

function Clerk() {
  const formRef = useRef(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [links, setLinks] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleting, setDeleting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [backendErrorMessage, setBackendErrorMessage] = useState('');
  const [selectedUser, setSelectedUser] = useState<User>(null);
  const [openInvoiceModal, setOpenInvoiceModal] = useState(false);
  // TODO: do not userUserResponse
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [total, setTotal] = useState(0);

  const {
    register, handleSubmit, formState: { errors }, reset,
  } = useForm<RegisterUser>({
    mode: 'onBlur',
    resolver: yupResolver(!selectedUser ? userSchema : userEditSchema),
    context: { isCreate: !!selectedUser?.id },
    criteriaMode: 'all',
    defaultValues: selectedUser,
  });

  const getUsers = useCallback((page?: number) => {
    if (!page) page = currentPage;
    user.get(page).then((res) => {
      if (res.data.data.length === 0 && currentPage > 1) {
        getUsers(currentPage - 1);
      } else {
        setUsers(res.data.data);
        setTotal(res.data.meta.total);
        setLinks(res.data.meta.links);
        setCurrentPage(res.data.meta.current_page);
      }
    }).catch(console.error);
  }, [currentPage]);

  useEffect(() => {
    getUsers();
  }, [getUsers]);

  const resetForm = useCallback(() => {
    reset({
      name: '',
      surname: '',
      mail: '',
      is_invoice_mode_enabled: false,
      user_roles: null,
      password: '',
      password_repeat: '',
    });
    setBackendErrorMessage('');
  }, [reset]);

  useEffect(() => {
    if (!selectedUser) {
      resetForm();
    } else {
      reset(selectedUser);
    }
  }, [resetForm, reset, selectedUser]);

  const onDelete = () => {
    setDeleting(true);
    user.destroy(selectedUser.id).then(() => {
      getUsers();
    }).finally(() => {
      setShowDeleteModal(false);
      setDeleting(false);
      setSelectedUser(null);
      resetForm();
    });
  };

  const postOk = () => {
    getUsers();
    resetForm();
    setShowCreateModal(false);
    setCreating(false);
  };

  const postNg = (err: any) => {
    setBackendErrorMessage(err?.response?.data?.error || '');
    setCreating(false);
  };

  const changePage = (page: number) => {
    getUsers(page);
  };

  const onSubmit = handleSubmit(async (data: RegisterUser) => {
    const update = () => {
      user.put({
        id: selectedUser.id,
        name: data.name,
        surname: data.surname,
        is_invoice_mode_enabled: data.is_invoice_mode_enabled,
        password: data.password,
        password_repeat: data.password_repeat,
      }).then(postOk).catch(postNg);
    };

    setCreating(true);
    if (selectedUser && selectedUser.id) {
      update();
    } else {
      user.post(data).then(postOk).catch(postNg);
    }
  });

  const handleInvoiceModalShow = () => {
    if (formRef.current?.elements.is_invoice_mode_enabled.checked) {
      setOpenInvoiceModal(true);
    }
  };

  return (
    <main className="content p-3">
      <ModalComponent
        loading={creating}
        errorMessage={backendErrorMessage}
        size="lg"
        title={selectedUser ? 'ユーザー編集' : 'ユーザー登録'}
        confirmButtonText="保存する"
        cancelButtonText="閉じる"
        cancelButtonColor="outline-secondary"
        onConfirm={() => submit(formRef)}
        show={showCreateModal}
        closeModal={() => { setSelectedUser(null); setShowCreateModal(false); }}
        scrollable
        style={{
          zIndex: 1050,
        }}
        centered
      >
        <div className=" m-3">
          <form
            onSubmit={onSubmit}
            ref={formRef}
          >
            <MultiInput
              title="お名前"
              isRequired
              label1="姓"
              name1="surname"
              label2="名"
              name2="name"
              register={register}
              errors={errors}
            />

            <Input disabled={!!selectedUser} isRequired title="メールアドレス" name="mail" error={errors?.mail?.message} register={register} />
            {!selectedUser && (
              <Select
                title="権限"
                name="user_roles"
                items={userRoleOptions}
                register={register}
                error={errors?.user_roles?.message}
              />
            )}
            <Input type="password" title="パスワード" name="password" error={errors?.password?.message} register={register} />
            <Input type="password" title="パスワード(確認用)" name="password_repeat" error={errors?.password_repeat?.message} register={register} />
            <div className="mb-3 row align-items-center">
              <label className="col-form-label col-sm-3 text-sm-start">お支払方法</label>
              <div className="col-sm-9">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span className="me-2">
                    <SwitchButton
                      defaultChecked={!!selectedUser && selectedUser.is_invoice_mode_enabled}
                      onClick={handleInvoiceModalShow}
                      {...register('is_invoice_mode_enabled', { valueAsNumber: !!selectedUser && !selectedUser.is_invoice_mode_enabled })}
                    />
                  </span>
                  請求書払い
                </div>
                <div className="text-sm" style={{ color: '#CC0000' }}>※請求書払いにして保存すると元に戻すことができませんのでご注意ください。</div>
              </div>
            </div>
          </form>
        </div>
      </ModalComponent>

      <ModalComponent
        loading={deleting}
        body={`${selectedUser?.name || ''}を削除してもよろしいですか？`}
        title="削除確認"
        confirmButtonText="削除"
        confirmButtonColor="danger"
        onConfirm={onDelete}
        show={showDeleteModal}
        closeModal={() => { setShowDeleteModal(false); setSelectedUser(null); }}
        centered
        animation={false}
      />

      <ModalComponent
        show={openInvoiceModal}
        closeModal={() => { setOpenInvoiceModal(false); formRef.current?.elements.is_invoice_mode_enabled.click(); }}
        title="請求払い確認"
        confirmButtonText="請求書払いにする"
        confirmButtonColor="danger"
        cancelButtonColor="outline-secondary"
        onConfirm={() => setOpenInvoiceModal(false)}
        centered
        backdrop
        backdropClassName="modal-backdrop fade show"
      >
        <div className="modal-body m-3">
          <p className="mb-0">請求書払いにしてもよろしいですか？</p>
          <p className="mb-0">請求書払いにして保存すると元に戻すことができません。ご注意ください。</p>
        </div>
      </ModalComponent>

      <Card title="ユーザー管理" subTitle="ユーザー一覧" onClickCreate={() => setShowCreateModal(true)}>
        <div className="row row-cols-auto g-1">
          {/* <Search onSearch={(text: any) => setSearchText(text)} /> */}
          <Paginator currentPage={currentPage} changePage={changePage} links={links} />
        </div>
        <Table>
          <thead>
            <tr>
              <th>ユーザーID</th>
              <th>ユーザー名</th>
              <th>メールアドレス</th>
              <th>権限</th>
              <th style={{ width: '9rem' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {total === 0 && (
              <tr>
                <td colSpan={10} className="text-center">データはありません。</td>
              </tr>
            )}
            {
              users && users.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{`${user.surname ? user.surname : ''} ${user.name}`}</td>
                  <td>{user.mail}</td>
                  <td>{user.user_roles}</td>
                  <td className="table-action text-center">
                    <ActionButton
                      onClickDelete={() => {
                        setSelectedUser({ ...user }); setShowDeleteModal(true);
                      }}
                      onClickEdit={() => {
                        setSelectedUser({ ...user });
                        setShowCreateModal(true);
                      }}
                    />
                  </td>
                </tr>
              ))
            }
          </tbody>
        </Table>
        <div className="row row-cols-auto g-1 align-items-center">
          <div className="col ">
            <div className="page-item-count text-sm">
              全
              {' '}
              <span>{total}</span>
              {' '}
              件
            </div>
          </div>
          <Paginator currentPage={currentPage} changePage={changePage} links={links} />
        </div>
      </Card>
    </main>
  );
}

export default Clerk;
